import {match} from 'ts-pattern';

import {FinancialProductType} from '@dms/api/shared';
import i18n from '@dms/i18n';

export type FinancingTemplateDetail = {label: string; value: string};

export type FinancingTemplateContent = {
  title: string;
  monthlyPaymentLabel: string;
  monthlyPayment: string;
  // Product fields first, the vehicle price last.
  details: FinancingTemplateDetail[];
};

// The BE financing-field catalogue prefixes every field key with the product.
const FIELD_KEY_PREFIX: Record<FinancialProductType, string> = {
  STANDARD: 'STANDARD_FINANCING_',
  BALLOON: 'BALLOON_FINANCING_',
  LEASING: 'LEASING_',
};

/**
 * Texts of a financing graphic template: labels in the user's language and `{{KEY}}` field tokens,
 * which the BE substitutes with the vehicle's values when it renders the promo photo.
 */
export const getFinancingTemplateContent = (
  financialProductType: FinancialProductType
): FinancingTemplateContent => {
  const field = (suffix: string) => `{{${FIELD_KEY_PREFIX[financialProductType]}${suffix}}}`;
  const termMonths = field('TERM_MONTHS');
  const term = (label: string): FinancingTemplateDetail => ({
    label,
    value: i18n.t('page.vehicle.financingAndLeasing.monthsValue', {term: termMonths}),
  });
  const downPayment: FinancingTemplateDetail = {
    label: i18n.t('page.vehicle.financingAndLeasing.downPayment'),
    value: field('DOWN_PAYMENT_AMOUNT'),
  };
  const vehiclePrice: FinancingTemplateDetail = {
    label: i18n.t('entity.vehicle.labels.carPrice'),
    value: field('VEHICLE_PRICE'),
  };

  const {title, productDetails} = match(financialProductType)
    .with('STANDARD', () => ({
      title: i18n.t('page.vehicle.financingAndLeasing.standard'),
      productDetails: [
        term(i18n.t('page.vehicle.financingAndLeasing.term')),
        downPayment,
        {label: i18n.t('page.vehicle.financingAndLeasing.apr'), value: field('APR')},
      ],
    }))
    .with('BALLOON', () => ({
      title: i18n.t('page.vehicle.financingAndLeasing.balloon'),
      productDetails: [
        term(i18n.t('page.vehicle.financingAndLeasing.term')),
        downPayment,
        {
          label: i18n.t('page.vehicle.financingAndLeasing.balloonPayment'),
          value: field('BALLOON_PAYMENT_AMOUNT'),
        },
      ],
    }))
    .with('LEASING', () => ({
      title: i18n.t('entity.vehicle.labels.leasing'),
      productDetails: [
        term(i18n.t('page.vehicle.financingAndLeasing.leasingTerm')),
        {
          label: i18n.t('entity.vehicle.labels.annualMileage'),
          value: i18n.t('page.vehicle.financingAndLeasing.mileagePerYear', {
            mileage: field('ANNUAL_MILEAGE'),
          }),
        },
        downPayment,
      ],
    }))
    .exhaustive();

  return {
    title,
    monthlyPaymentLabel: i18n.t('entity.promoPhotos.composition.templates.monthlyPayment'),
    monthlyPayment: field('MONTHLY_PAYMENT'),
    details: [...productDetails, vehiclePrice],
  };
};
